import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { worksheetService, type Worksheet } from "@/services/worksheet.service";
import {
  curriculumService,
  type CurriculumOutcome,
} from "@/services/curriculum.service";
import {
  WorksheetCard,
  WorksheetCardSkeleton,
} from "@/components/worksheets/WorksheetCard";
import { WorksheetCurriculumView } from "@/components/worksheets/WorksheetCurriculumView";
import {
  FileText,
  X,
  RefreshCcw,
  LayoutGrid,
  BookOpen,
  User,
} from "lucide-react";
import { useUserStore } from "@/store/userStore";
import {
  personalizeWorksheets,
  sortByRelevance,
  hasPreferences,
  type PersonalizedWorksheet,
  type TeacherPreferences,
} from "@/lib/personalization";

// ============================================================================
// Constants
// ============================================================================

const GRADE_OPTIONS = [
  { value: "all", label: "All Grades" },
  { value: "K", label: "Kindergarten" },
  { value: "1", label: "Grade 1" },
  { value: "2", label: "Grade 2" },
  { value: "3", label: "Grade 3" },
  { value: "4", label: "Grade 4" },
  { value: "5", label: "Grade 5" },
  { value: "6", label: "Grade 6" },
];

const SUBJECT_OPTIONS = [
  { value: "all", label: "All Subjects" },
  { value: "mathematics", label: "Mathematics" },
  { value: "social_studies", label: "Social Studies" },
  { value: "english_language_arts", label: "English Language Arts" },
  { value: "science", label: "Science" },
];

type ViewType = "browse" | "curriculum";

interface QuickFilters {
  myGrades: boolean;
  mySubjects: boolean;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if an error is a request cancellation (AbortError).
 */
function isRequestCancelled(error: unknown): boolean {
  if (error instanceof DOMException && error.name === "AbortError") {
    return true;
  }
  // Axios wraps abort errors
  if (
    error &&
    typeof error === "object" &&
    "code" in error &&
    (error as { code: string }).code === "ERR_CANCELED"
  ) {
    return true;
  }
  return false;
}

// ============================================================================
// Skeleton Components
// ============================================================================

function CurriculumViewSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Skeleton className="h-5 w-5" />
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-5 w-24 ml-auto" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Quick Filter Chips
// ============================================================================

interface QuickFilterChipsProps {
  filters: QuickFilters;
  onToggle: (filter: keyof QuickFilters) => void;
  preferences: TeacherPreferences;
}

function QuickFilterChips({
  filters,
  onToggle,
  preferences,
}: QuickFilterChipsProps) {
  if (!hasPreferences(preferences)) {
    return null;
  }

  const gradeCount = preferences.gradeLevels.length;
  const subjectCount = preferences.subjects.length;

  return (
    <div className="flex flex-wrap gap-2">
      {gradeCount > 0 && (
        <Button
          variant={filters.myGrades ? "default" : "outline"}
          size="sm"
          onClick={() => onToggle("myGrades")}
          className="gap-1"
        >
          <User className="h-3 w-3" />
          My Grades
          <Badge variant={filters.myGrades ? "secondary" : "outline"} className="ml-1 h-5 px-1.5">
            {gradeCount}
          </Badge>
        </Button>
      )}
      {subjectCount > 0 && (
        <Button
          variant={filters.mySubjects ? "default" : "outline"}
          size="sm"
          onClick={() => onToggle("mySubjects")}
          className="gap-1"
        >
          <BookOpen className="h-3 w-3" />
          My Subjects
          <Badge variant={filters.mySubjects ? "secondary" : "outline"} className="ml-1 h-5 px-1.5">
            {subjectCount}
          </Badge>
        </Button>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function Worksheets() {
  // URL-driven tab state
  const [searchParams, setSearchParams] = useSearchParams();

  // Get user profile for personalization
  const profile = useUserStore((s) => s.profile);

  // Extract teacher preferences
  const preferences = useMemo<TeacherPreferences>(
    () => ({
      gradeLevels: profile?.gradeLevels ?? [],
      subjects: profile?.subjects ?? [],
    }),
    [profile?.gradeLevels, profile?.subjects]
  );

  // Get current view from URL, default to "browse"
  const currentView = useMemo<ViewType>(() => {
    const view = searchParams.get("view");
    return view === "curriculum" ? "curriculum" : "browse";
  }, [searchParams]);

  // Filter state
  const [gradeFilter, setGradeFilter] = useState("all");
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [quickFilters, setQuickFilters] = useState<QuickFilters>({ myGrades: false, mySubjects: false });
  const [filtersInitialized, setFiltersInitialized] = useState(false);

  // Initialize quick filters based on user preferences (once profile loads)
  useEffect(() => {
    if (!filtersInitialized && profile) {
      const hasGrades = (profile.gradeLevels?.length ?? 0) > 0;
      const hasSubjects = (profile.subjects?.length ?? 0) > 0;
      if (hasGrades || hasSubjects) {
        setQuickFilters({ myGrades: hasGrades, mySubjects: hasSubjects });
      }
      setFiltersInitialized(true);
    }
  }, [profile, filtersInitialized]);

  // Worksheet data state
  const [worksheets, setWorksheets] = useState<Worksheet[]>([]);
  const [worksheetsLoading, setWorksheetsLoading] = useState(true);
  const [worksheetsError, setWorksheetsError] = useState<string | null>(null);

  // Curriculum data state (lazy-loaded)
  const [outcomes, setOutcomes] = useState<CurriculumOutcome[]>([]);
  const [outcomesLoading, setOutcomesLoading] = useState(false);
  const [outcomesError, setOutcomesError] = useState<string | null>(null);
  const [outcomesLoaded, setOutcomesLoaded] = useState(false);

  // Refs for cleanup
  const retryControllerRef = useRef<AbortController | null>(null);

  // Derived state
  const hasFilters = gradeFilter !== "all" || subjectFilter !== "all";

  // Personalize worksheets with teacher preferences
  const personalizedWorksheets = useMemo<PersonalizedWorksheet[]>(() => {
    const personalized = personalizeWorksheets(worksheets, preferences);
    return sortByRelevance(personalized);
  }, [worksheets, preferences]);

  // Apply quick filters (accumulative)
  const filteredWorksheets = useMemo<PersonalizedWorksheet[]>(() => {
    const { myGrades, mySubjects } = quickFilters;

    // No filters active - show all
    if (!myGrades && !mySubjects) {
      return personalizedWorksheets;
    }

    // Both filters active - OR logic (matches grade OR subject)
    if (myGrades && mySubjects) {
      return personalizedWorksheets.filter((w) => w.matchesGrade || w.matchesSubject);
    }

    // Only grade filter
    if (myGrades) {
      return personalizedWorksheets.filter((w) => w.matchesGrade);
    }

    // Only subject filter
    return personalizedWorksheets.filter((w) => w.matchesSubject);
  }, [personalizedWorksheets, quickFilters]);

  // Check if any quick filters are active
  const hasQuickFilters = quickFilters.myGrades || quickFilters.mySubjects;

  // Handle tab change with URL sync
  const handleViewChange = useCallback(
    (value: string) => {
      const newParams = new URLSearchParams(searchParams);
      if (value === "browse") {
        newParams.delete("view");
      } else {
        newParams.set("view", value);
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Fetch worksheets
  const fetchWorksheets = useCallback(
    async (signal: AbortSignal) => {
      try {
        setWorksheetsLoading(true);
        setWorksheetsError(null);

        const filters: { grade?: string; subject?: string } = {};
        if (gradeFilter && gradeFilter !== "all") filters.grade = gradeFilter;
        if (subjectFilter && subjectFilter !== "all") filters.subject = subjectFilter;

        const response = await worksheetService.getWorksheets(filters, signal);

        if (!signal.aborted) {
          setWorksheets(response.items);
        }
      } catch (err) {
        if (!isRequestCancelled(err)) {
          console.error("Failed to load worksheets:", err);
          setWorksheetsError(
            err instanceof Error ? err.message : "Failed to load worksheets"
          );
        }
      } finally {
        if (!signal.aborted) {
          setWorksheetsLoading(false);
        }
      }
    },
    [gradeFilter, subjectFilter]
  );

  // Fetch curriculum outcomes (lazy-loaded)
  const fetchOutcomes = useCallback(async (signal: AbortSignal) => {
    try {
      setOutcomesLoading(true);
      setOutcomesError(null);

      const result = await curriculumService.getOutcomes(signal);

      if (!signal.aborted) {
        setOutcomes(result);
        setOutcomesLoaded(true);
      }
    } catch (err) {
      if (!isRequestCancelled(err)) {
        console.error("Failed to load curriculum outcomes:", err);
        setOutcomesError(
          err instanceof Error ? err.message : "Failed to load curriculum"
        );
      }
    } finally {
      if (!signal.aborted) {
        setOutcomesLoading(false);
      }
    }
  }, []);

  // Fetch worksheets on mount and filter change
  useEffect(() => {
    const controller = new AbortController();
    fetchWorksheets(controller.signal);
    return () => {
      controller.abort();
      retryControllerRef.current?.abort();
    };
  }, [fetchWorksheets]);

  // Lazy-load curriculum when curriculum tab is first selected
  const outcomesControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (currentView === "curriculum" && !outcomesLoaded) {
      // Avoid duplicate fetches
      if (outcomesControllerRef.current) return;

      const controller = new AbortController();
      outcomesControllerRef.current = controller;

      fetchOutcomes(controller.signal).finally(() => {
        outcomesControllerRef.current = null;
      });

      return () => {
        controller.abort();
        outcomesControllerRef.current = null;
      };
    }
  }, [currentView, outcomesLoaded, fetchOutcomes]);

  // Clear all filters
  const handleClearFilters = () => {
    setGradeFilter("all");
    setSubjectFilter("all");
    setQuickFilters({ myGrades: false, mySubjects: false });
  };

  // Toggle quick filter (accumulative)
  const handleToggleQuickFilter = (filter: keyof QuickFilters) => {
    setQuickFilters((prev) => ({ ...prev, [filter]: !prev[filter] }));
    // Clear dropdown filters when using quick filters
    setGradeFilter("all");
    setSubjectFilter("all");
  };

  // Retry worksheets after error
  const handleRetryWorksheets = () => {
    retryControllerRef.current?.abort();
    const controller = new AbortController();
    retryControllerRef.current = controller;
    fetchWorksheets(controller.signal);
  };

  // Retry curriculum after error
  const handleRetryCurriculum = () => {
    const controller = new AbortController();
    fetchOutcomes(controller.signal);
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Worksheets</h1>
        <p className="text-muted-foreground">
          Browse and select worksheets for your classroom
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={currentView} onValueChange={handleViewChange}>
        <TabsList>
          <TabsTrigger value="browse" className="gap-2">
            <LayoutGrid className="h-4 w-4" />
            Browse All
          </TabsTrigger>
          <TabsTrigger value="curriculum" className="gap-2">
            <BookOpen className="h-4 w-4" />
            By Curriculum
          </TabsTrigger>
        </TabsList>

        {/* Browse All Tab */}
        <TabsContent value="browse" className="mt-6 space-y-6">
          {/* Quick Filter Chips */}
          {hasPreferences(preferences) && (
            <QuickFilterChips
              filters={quickFilters}
              onToggle={handleToggleQuickFilter}
              preferences={preferences}
            />
          )}

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4">
            <Select value={gradeFilter} onValueChange={setGradeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Grades" />
              </SelectTrigger>
              <SelectContent>
                {GRADE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={subjectFilter} onValueChange={setSubjectFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Subjects" />
              </SelectTrigger>
              <SelectContent>
                {SUBJECT_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {(hasFilters || hasQuickFilters) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilters}
                className="text-muted-foreground"
              >
                <X className="mr-1 h-4 w-4" />
                Clear filters
              </Button>
            )}
          </div>

          {/* Error State */}
          {worksheetsError && !worksheetsLoading && (
            <Card className="border-destructive/50">
              <CardContent className="py-8 text-center">
                <p className="text-destructive mb-4">{worksheetsError}</p>
                <Button variant="outline" onClick={handleRetryWorksheets}>
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  Try Again
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Loading State */}
          {worksheetsLoading && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <WorksheetCardSkeleton key={i} />
              ))}
            </div>
          )}

          {/* Empty State */}
          {!worksheetsLoading && !worksheetsError && filteredWorksheets.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-semibold mb-2">No worksheets found</h3>
                <p className="text-muted-foreground mb-4">
                  {hasFilters || hasQuickFilters
                    ? "Try adjusting your filters to find worksheets."
                    : "No worksheets are available yet."}
                </p>
                {(hasFilters || hasQuickFilters) && (
                  <Button variant="outline" onClick={handleClearFilters}>
                    <X className="mr-2 h-4 w-4" />
                    Clear Filters
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Worksheet Grid */}
          {!worksheetsLoading && !worksheetsError && filteredWorksheets.length > 0 && (
            <>
              {/* Section header when showing filtered results */}
              {hasQuickFilters && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>
                    Showing {filteredWorksheets.length} worksheet
                    {filteredWorksheets.length !== 1 ? "s" : ""} matching your{" "}
                    {quickFilters.myGrades && quickFilters.mySubjects
                      ? "grades and subjects"
                      : quickFilters.myGrades
                      ? "grades"
                      : "subjects"}
                  </span>
                </div>
              )}
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {filteredWorksheets.map((worksheet) => (
                  <WorksheetCard key={worksheet.id} worksheet={worksheet} />
                ))}
              </div>
            </>
          )}
        </TabsContent>

        {/* Curriculum Tab */}
        <TabsContent value="curriculum" className="mt-6">
          {/* Error State */}
          {outcomesError && !outcomesLoading && (
            <Card className="border-destructive/50">
              <CardContent className="py-8 text-center">
                <p className="text-destructive mb-4">{outcomesError}</p>
                <Button variant="outline" onClick={handleRetryCurriculum}>
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  Try Again
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Loading State */}
          {(outcomesLoading || worksheetsLoading) && <CurriculumViewSkeleton />}

          {/* Curriculum View */}
          {!outcomesLoading &&
            !worksheetsLoading &&
            !outcomesError &&
            !worksheetsError && (
              <WorksheetCurriculumView
                worksheets={worksheets}
                outcomes={outcomes}
              />
            )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
